import {
	BasicButton,
	Loading,
	SecondCustomInput,
	Typography,
} from "@/components";
import { ExternalInvitations } from "@/components/ui/invitations";
import { QUERY_KEYS } from "@/libraries/constant";
import { isAxiosError, isMpisAccount } from "@/libraries/helper";
import {
	getCaseQueryOptions,
	getMembersQueryOptions,
	getMpisMembersQueryOptions,
} from "@/queries";
import {
	type CaseReferralFormFieldSchema,
	ReferralMemberSearchParamSchema,
} from "@/schemas";
import SearchIcon from "@mui/icons-material/Search";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { onlineManager, useQuery } from "@tanstack/react-query";
import { ErrorComponent, Link, createFileRoute } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import omit from "lodash/omit";
import { Suspense, lazy, useCallback, useEffect } from "react";
import { isMobile } from "react-device-detect";
import { useFormContext } from "react-hook-form";
import ReferralContactError from "./-components/ContactError";
const ScrollToTopButton = lazy(() => import("./-components/ScrollToTop"));

export const Route = createFileRoute("/dashboard/case/referrals/contact")({
	validateSearch: zodSearchValidator(ReferralMemberSearchParamSchema),
	loaderDeps: ({ search }) => ({
		tab: search.tab,
	}),
	loader: ({ context, deps }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			return [
				...(deps.tab !== "mpis"
					? [
							context.queryClient.ensureInfiniteQueryData(
								getMembersQueryOptions({
									directory: deps.tab === "directory",
								}),
							),
						]
					: [
							context.queryClient.ensureInfiniteQueryData(
								getMpisMembersQueryOptions(),
							),
						]),
			];
		}
		return [
			...(deps.tab !== "mpis"
				? [
						context.queryClient.getQueryData([
							QUERY_KEYS.GET_MEMBERS,
							{
								directory: deps.tab === "directory",
							},
						]),
						context.queryClient.getQueryData([
							QUERY_KEYS.GET_TEAMS,
							{
								directory: deps.tab === "directory",
								scope: "case_teams",
							},
						]),
					]
				: [context.queryClient.getQueryData([QUERY_KEYS.GET_MPIS_MEMBERS])]),
		];
	},
	component: RouteComponent,
	errorComponent: ({ error }) =>
		error && isAxiosError(error, 403) ? (
			<ReferralContactError />
		) : (
			<ErrorComponent error={error} />
		),
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const searchQ = Route.useSearch({
		select: (s: ReferralMemberSearchParamSchema) => s.q ?? "",
	});
	const tab = Route.useSearch({
		select: (s: ReferralMemberSearchParamSchema) => s.tab,
	});
	const subtab = Route.useSearch({
		select: (s: ReferralMemberSearchParamSchema) => s.subtab,
	});
	const { watch, setValue } = useFormContext<CaseReferralFormFieldSchema>();
	const caseId = watch("caseId");
	const selectedTeam = watch("team");
	const selectedMember = watch("member");
	const tags = watch("tags");

	const caseQuery = useQuery(getCaseQueryOptions(+caseId));

	const handleChangeTab = (e: React.SyntheticEvent, newValue: string) => {
		e.preventDefault();
		return navigate({
			replace: true,
			search: (oldSearch: ReferralMemberSearchParamSchema) => ({
				...omit(oldSearch, ["tab"]),
				tab: newValue,
			}),
		});
	};

	const handleChangeSubTab = (e: React.SyntheticEvent, newValue: string) => {
		e.preventDefault();
		return navigate({
			replace: true,
			search: (oldSearch: ReferralMemberSearchParamSchema) => ({
				...omit(oldSearch, ["subtab", "q"]),
				subtab: newValue,
			}),
		});
	};

	const handleOnChange = useCallback(
		(
			e: React.ChangeEvent<HTMLInputElement>,
			val: any,
			type: "teams" | "contacts",
		) => {
			e.preventDefault();
			// @ts-expect-error
			setValue(type === "teams" ? "member" : "team", null);
			return setValue(type === "teams" ? "team" : "member", val, {
				shouldDirty: true,
				shouldTouch: true,
				shouldValidate: true,
			});
		},
		[setValue],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (!tags) {
			navigate({
				replace: true,
				to: "/dashboard/case/referrals/type",
			});
		}
	}, [tags]);

	return (
		<>
			<div className="container flex flex-col gap-4">
				<Typography fontWeight={900} fontSize={14}>
					Select one of your contact:
				</Typography>
				<Tabs
					centered
					value={tab}
					variant="fullWidth"
					textColor="inherit"
					aria-label="Contact Type"
					className="!rounded-full !bg-gray-light [&_.MuiTabs-indicator]:!bg-transparent !p-1"
					onChange={handleChangeTab}
				>
					<Tab
						value="contact"
						label="Contacts"
						classes={{
							selected: "!bg-white !rounded-full",
						}}
					/>
					<Tab
						value="directory"
						label="Directory"
						classes={{
							selected: "!bg-white !rounded-full",
						}}
					/>
					{isMpisAccount() ? (
						<Tab
							value="mpis"
							label="MPIS"
							classes={{
								selected: "!bg-white !rounded-full",
							}}
						/>
					) : null}
				</Tabs>
				{tab !== "mpis" ? (
					<Tabs
						value={subtab}
						variant="fullWidth"
						textColor="inherit"
						aria-label="Contact Sub Type"
						className="[&_.MuiTabs-indicator]:bg-primary"
						onChange={handleChangeSubTab}
					>
						<Tab value="team" label="Teams" style={{ textTransform: "none" }} />
						<Tab
							value="contact"
							label="Contacts"
							style={{ textTransform: "none" }}
						/>
					</Tabs>
				) : null}
				<SecondCustomInput
					type="search"
					name="search"
					placeholder="Search"
					startIcon={<SearchIcon />}
					value={searchQ}
					onChange={(e) =>
						navigate({
							search: (oldSearch: ReferralMemberSearchParamSchema) => ({
								...omit(oldSearch, ["q"]),
								q: e.target.value || undefined,
							}),
							replace: true,
						})
					}
				/>
			</div>
			<ExternalInvitations
				tab={tab}
				subTab={subtab}
				search={searchQ}
				caseItem={caseQuery.data}
				selectedTeamId={selectedTeam?.id}
				selectedMemberId={selectedMember?.id || selectedMember?.aidbox_id}
				onChange={handleOnChange}
				isMobile={isMobile}
			/>
			<Suspense>{isMobile ? <ScrollToTopButton /> : null}</Suspense>
			<div className="container grid grid-cols-2 gap-2 bg-white py-4 md:pb-4 md:pt-0 sticky md:relative bottom-0">
				<Link to="/dashboard/case/referrals/type">
					<BasicButton
						variant="outlined"
						color="secondary"
						className="!h-12 text-sm font-bold !shadow-none"
						fullWidth
					>
						Back
					</BasicButton>
				</Link>
				<BasicButton
					variant="contained"
					color="secondary"
					className="!h-12 text-sm font-bold !shadow-none"
					fullWidth
					disabled={!selectedTeam && !selectedMember}
					onClick={() =>
						navigate({
							to: "/dashboard/case/referrals/message",
						})
					}
				>
					Next
				</BasicButton>
			</div>
			<Loading status={caseQuery.isLoading} />
		</>
	);
}
